import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { menuData, menuTabs } from "../data/menuData";
import { ASSETS } from "../data/constants";

const MenuItem = ({ item, lang, t }) => {
    const name = lang === "gr" && item.gr ? item.gr : item.en;
    const descObj = item.desc;
    const desc = descObj ? (lang === "gr" && descObj.gr ? descObj.gr : descObj.en) : "";
    return (
        <div
            className={`py-4 flex items-start justify-between gap-4 border-b border-aegean-100 last:border-b-0 ${
                item.unavailable ? "opacity-50" : ""
            }`}
        >
            <div className="flex-1">
                <div className="flex items-baseline gap-3 flex-wrap">
                    <h4 className="font-display uppercase tracking-[0.08em] text-base md:text-lg font-semibold text-aegean-800">
                        {name}
                    </h4>
                    {item.unavailable && (
                        <span className="font-display uppercase tracking-[0.2em] text-[9px] px-2 py-0.5 bg-aegean-100 text-aegean-700 rounded-sm">
                            {t("menu.unavailable")}
                        </span>
                    )}
                    <div className="flex-1 border-b border-dotted border-aegean-300 mb-1.5 hidden md:block" />
                </div>
                {desc && <p className="mt-1 text-sm text-ink/65 leading-relaxed max-w-xl">{desc}</p>}
            </div>
            <div className="font-display text-base md:text-lg font-semibold text-aegean-600 whitespace-nowrap">
                {item.price === "—" ? "" : `${item.price} €`}
            </div>
        </div>
    );
};

const MenuPage = () => {
    const { t, lang } = useLang();
    const [tab, setTab] = useState("food");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const visibleSections = useMemo(() => menuTabs[tab] || [], [tab]);

    return (
        <div data-testid="menu-page" className="pt-32 pb-24 bg-ivory min-h-screen">
            {/* Header */}
            <section className="container-x text-center mb-16">
                <p className="eyebrow">{t("menu.subtitle")}</p>
                <h1 className="headline text-aegean-700 text-[18vw] sm:text-[12vw] lg:text-[160px] leading-none mt-3">
                    {t("menu.title")}
                </h1>
                <div className="mt-6 flex justify-center">
                    <div className="h-px w-24 bg-aegean-300" />
                </div>
            </section>

            {/* Tabs */}
            <section className="container-x mb-12">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                    {Object.keys(menuTabs).map((id) => (
                        <button
                            key={id}
                            data-testid={`menu-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`btn-pill ${
                                tab === id
                                    ? "bg-aegean-500 text-white"
                                    : "bg-white text-aegean-700 border border-aegean-200 hover:bg-aegean-50"
                            }`}
                        >
                            {t(`menu.tabs.${id}`)}
                        </button>
                    ))}
                </div>
            </section>

            {/* Sections */}
            <section className="container-x">
                <div className="grid lg:grid-cols-2 gap-x-16 gap-y-16">
                    {visibleSections.map((key) => {
                        const section = menuData[key];
                        if (!section) return null;
                        return (
                            <div key={key} data-testid={`menu-section-${key}`}>
                                <h2 className="menu-section-title">
                                    {t(`menu.categories.${section.nameKey}`)}
                                </h2>
                                <div>
                                    {section.items.map((item, idx) => (
                                        <MenuItem key={idx} item={item} lang={lang} t={t} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Editorial gallery */}
            <section className="mt-28">
                <div className="container-x">
                    <div className="text-center mb-12">
                        <p className="eyebrow">{lang === "gr" ? "Από την κουζίνα" : "From the kitchen"}</p>
                        <h3 className="mt-4 font-accent italic font-light text-aegean-800 text-3xl md:text-4xl">
                            {lang === "gr" ? "Όπως σερβίρεται" : "As served"}
                        </h3>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-3 px-3 md:px-6">
                    <div className="col-span-12 md:col-span-5 aspect-[4/5] overflow-hidden">
                        <img src={ASSETS.venueEntrance} alt="AMOS seaside entrance" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                    </div>
                    <div className="col-span-12 md:col-span-7 grid grid-cols-2 gap-3">
                        <div className="aspect-square overflow-hidden">
                            <img src={ASSETS.steakPlate} alt="Steak with salad" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                        </div>
                        <div className="aspect-square overflow-hidden">
                            <img src={ASSETS.dessertCushion} alt="Signature dessert" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                        </div>
                        <div className="aspect-square overflow-hidden">
                            <img src={ASSETS.miniBurgers} alt="AMOS mini burgers" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                        </div>
                        <div className="aspect-square overflow-hidden">
                            <img src={ASSETS.cocktails} alt="Cocktails by the sea" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MenuPage;
